/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import PegaConstants from '../constants';
import Utils from '../utils/utils';

import type { RefreshMap } from './types';

/**
 * @description
 * Exposes APIs to register properties and case updates and triggers explicit refresh
 * Deregisters registration upon removal of context
 */

class RefreshManager {
  public readonly refreshMap: RefreshMap;
  public readonly propChangeRefreshMap: RefreshMap;

  constructor() {
    this.refreshMap = {};
    this.propChangeRefreshMap = {};
  }

  /**
   * Initialize cache
   * @description This method initializes refresh cache
   * @param cachedMap - refreshMap or propChangeRefreshMap passed
   * @param context - Context where assignment is loaded
   * @param propOrTabToRegister - Property name or case view tab to be registered
   * @param uniqueHash - Unique key to avoid duplication
   * @param callback - Bound Callback to invoked later
   *
   * @example <caption>Example for initializeRefreshCache()</caption>
   * Example can be seen in Refresh Manager registerForRefresh method
   * Example usage - RefreshManager.initializeRefreshCache()
   *
   * @see {@link initializeRefreshCache} for further information.
   * @private
   * @function
   */
  static initializeRefreshCache(
    cachedMap: RefreshMap,
    context: string,
    propOrTabToRegister: string,
    uniqueHash: string,
    callback: Function
  ) {
    if (typeof cachedMap[context] === 'undefined') {
      cachedMap[context] = {};
    }
    cachedMap[context][propOrTabToRegister] = [{ uniqueHash, callback }];
  }

  /**
   * Register property to a cache
   * @description This method register a property to refresh cache
   * @param refreshType - Specifies the type of refresh PROP_CHANGE / CASE_UPDATE
   * @param callback - Bound Callback to invoked later
   * @param propOrTabToRegister - Full Property reference or particular case view tab to be registered
   * @param uniqueIds - Combines context and page reference etc
   * @param context - Context where assignment is loaded

   *
   * @example <caption>Example for registerForRefresh()</caption>
   * Example can be seen in Actions api registerForRefreshView method
   * Example usage -  PCore.getRefreshManager().registerForRefresh(refreshType, callback, propChanged, id, context);
   *
   * @see {@link registerForRefresh} for further information.
   * @private
   * @function
   */
  registerForRefresh(
    refreshType: string,
    callback: Function,
    propOrTabToRegister: string,
    uniqueIds: string,
    context: string
  ) {
    propOrTabToRegister = Utils.convertClipboardPageToPageRefNotation(propOrTabToRegister);
    const uniqueList = uniqueIds.split('/').join(PegaConstants.HASH_DELIMITER);
    const uniqueHash = Utils.getHashedString(uniqueList);
    if (refreshType === 'PROP_CHANGE') {
      if (this.propChangeRefreshMap?.[context]?.[propOrTabToRegister]) {
        const hashExists = this.propChangeRefreshMap[context][propOrTabToRegister].some(
          (element) => element.uniqueHash === uniqueHash
        );
        if (!hashExists) {
          this.propChangeRefreshMap[context][propOrTabToRegister].push({ uniqueHash, callback });
        }
      } else {
        RefreshManager.initializeRefreshCache(
          this.propChangeRefreshMap,
          context,
          propOrTabToRegister,
          uniqueHash,
          callback
        );
      }
    } else if (this.refreshMap?.[context]?.[`${refreshType}-${propOrTabToRegister}`]) {
      const hashExists = this.refreshMap[context][`${refreshType}-${propOrTabToRegister}`].some(
        (element) => element.uniqueHash === uniqueHash
      );
      if (!hashExists) {
        this.refreshMap[context][`${refreshType}-${propOrTabToRegister}`].push({ uniqueHash, callback });
      }
    } else {
      RefreshManager.initializeRefreshCache(
        this.refreshMap,
        context,
        `${refreshType}-${propOrTabToRegister}`,
        uniqueHash,
        callback
      );
    }
  }

  /**
   * Trigger refresh callback
   * @description This method finds refresh callback registered to passed property
   * @param refreshType - Specifies the type of refresh PROP_CHANGE / CASE_UPDATE
   * @param propOrTabChanged - Full Property reference callback is register for or case view tab to be refreshed
   * @param context - Context where assignment is loaded

   *
   * @example <caption>Example for triggerRefreshForType()</caption>
   * Example can be seen in Actions api triggerRefreshViewForType method
   * Example usage - PCore.getRefreshManager().triggerRefreshForType(type, propOrTabChanged, context);
   *
   * @see {@link triggerRefreshForType} for further information.
   * @private
   * @function
   */
  triggerRefreshForType(refreshType: string, propOrTabChanged: string, context: string) {
    if (refreshType === 'PROP_CHANGE') {
      if (this.propChangeRefreshMap?.[context]?.[propOrTabChanged]) {
        this.propChangeRefreshMap[context][propOrTabChanged].forEach((callbackObj) => callbackObj.callback());
      }
    } else if (this.refreshMap?.[context]?.[`${refreshType}-${propOrTabChanged}`]) {
      this.refreshMap[context][`${refreshType}-${propOrTabChanged}`].forEach((callbackObj) => callbackObj.callback());
    }
  }

  /**
   * Removes cache per context
   * @description This method Removes cache as per given context
   * @param context - Context where is getting removed
   *
   * @example <caption>Example for deRegisterForRefresh()</caption>
   * Example can be seen in Garbage Collector cleanCaches method
   * Example usage - PCore.getRefreshManager().deRegisterForRefresh(context);
   *
   * @see {@link deRegisterForRefresh} for further information.
   * @private
   * @function
   */
  deRegisterForRefresh(context: string) {
    delete this.propChangeRefreshMap[context];
    delete this.refreshMap[context];
  }
}

export default new RefreshManager();
